import { ChevronDown } from "lucide-react";
import { PHOTO_DETAILS, PLASTICS_FAQS, PLASTICS_MESSAGE, PLASTICS_QUOTE_PATH, PROCESS_STEPS } from "../../data/plastics";
import { RECYCLING_IMAGES } from "../../data/recyclingImages";
import { MaterialPhoto } from "./MaterialPhoto";
import { PhotoEnquiry } from "./PhotoEnquiry";

const PROCESS_IMAGES = [RECYCLING_IMAGES.assessment, RECYCLING_IMAGES.collection, RECYCLING_IMAGES.shredding];

export const PlasticsSection = ({ id, title, children, shaded = false }) => (
  <section data-testid={`plastics-${id}`} className={`${shaded ? "bg-[#F2F2F2]" : "bg-white"} py-16 md:py-24`}>
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <h2 data-testid={`plastics-${id}-heading`} className="font-display font-bold text-base md:text-lg text-[#024120] mb-8">{title}</h2>
      {children}
    </div>
  </section>
);

export const PlasticsList = ({ items, id, columns = true }) => (
  <ul data-testid={id} className={`grid ${columns ? "md:grid-cols-2" : "grid-cols-1"} gap-x-10 gap-y-3 text-sm md:text-base leading-relaxed text-[#4A4A4A]`}>
    {items.map((item, i) => <li key={item} data-testid={`${id}-${i + 1}`} className="border-l-2 border-[#46B649] pl-4">{item}</li>)}
  </ul>
);

export const PlasticsProcess = () => (
  <PlasticsSection id="process" title="How we process suitable rigid plastics">
    <ol className="space-y-12 md:space-y-16">
      {PROCESS_STEPS.map((step, i) => (
        <li key={step.title} data-testid={`plastics-process-step-${i + 1}`} className="border-t border-[#024120]/20 pt-8 grid md:grid-cols-2 items-start gap-8 md:gap-12">
          {PROCESS_IMAGES[i] && <MaterialPhoto image={PROCESS_IMAGES[i]} testId={`plastics-process-image-${i + 1}`} />}
          <div className={PROCESS_IMAGES[i] ? "min-w-0" : "md:col-span-2 max-w-4xl"}>
            <h3 className="font-display font-bold text-lg text-[#024120]"><span className="text-[#a83f00] mr-3">0{i + 1}</span>{step.title}</h3>
            <p className="mt-4 mb-5 text-sm md:text-base text-[#4A4A4A] leading-relaxed">{step.intro}</p>
            {step.items && <PlasticsList items={step.items} id={`plastics-process-list-${i + 1}`} columns={false} />}
            {step.note && <p className="mt-5 text-sm md:text-base text-[#4A4A4A] leading-relaxed">{step.note}</p>}
          </div>
        </li>
      ))}
    </ol>
  </PlasticsSection>
);

export const PlasticsFaqs = () => (
  <PlasticsSection id="faqs" title="Frequently asked questions" shaded>
    <div className="max-w-4xl space-y-3">
      {PLASTICS_FAQS.map((faq, i) => (
        <details key={faq.q} data-testid={`plastics-faq-${i + 1}`} className="group bg-white border border-[#024120]/10">
          <summary data-testid={`plastics-faq-toggle-${i + 1}`} className="cursor-pointer list-none flex justify-between gap-4 px-6 py-5 font-display font-bold text-[#024120]">
            {faq.q}<ChevronDown aria-hidden="true" className="w-5 h-5 shrink-0 transition-transform group-open:rotate-180" />
          </summary>
          <p data-testid={`plastics-faq-answer-${i + 1}`} className="px-6 pb-6 text-sm md:text-base text-[#4A4A4A] leading-relaxed">{faq.a}</p>
        </details>
      ))}
    </div>
  </PlasticsSection>
);

export const PlasticsPhotoContact = () => (
  <PhotoEnquiry id="plastics" title="Send us a photograph"
    intro="The quickest way to find out whether we can accept your material is to send us:"
    details={PHOTO_DETAILS} message={PLASTICS_MESSAGE} quoteTo={PLASTICS_QUOTE_PATH} />
);